/**
 * @fileoverview Cabeceras que Apigee exige en toda llamada a la API de Zona Segura: `x-api-key` y las
 * cuatro de trazabilidad. Si falta una de las cuatro, Apigee responde 400 `missing_trace_headers`; sin key, 401.
 * No aplica a los links que abre el navegador (descargas) ni al callback de Niubiz: no pueden llevar cabeceras.
 * @module utils/apigeeHeaders
 */

import environment, { APPLICATION_ID } from './environment'
import { isZsBackendRequest } from './authInterceptors'

/** Proceso de negocio al que pertenece la llamada (valor por confirmar con Arquitectura). */
export const PROCESS_ID = 'zonasegura-portal'

/** Cadena de artefactos: la solicitud nace en este frontend. */
export const ARTIFACT_CHAIN = 'zonasegura-frontend'

/**
 * Genera un identificador único por request (UUID v4).
 * @returns {string}
 */
export function newTransactionId () {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }
  const bytes = new Uint8Array(16)
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.prototype.map.call(bytes, byte => ('0' + byte.toString(16)).slice(-2)).join('')
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-')
}

/**
 * Arma las cabeceras de Apigee para una llamada. `x-api-key` solo se incluye si hay key configurada.
 * @returns {Object}
 */
export function buildApigeeHeaders () {
  const headers = {
    'Transaction-ID': newTransactionId(),
    'Application-ID': APPLICATION_ID,
    'Process-ID': PROCESS_ID,
    'Artifact-Chain': ARTIFACT_CHAIN
  }
  const apiKey = environment.getApiKey()
  if (apiKey) {
    headers['x-api-key'] = apiKey
  }
  return headers
}

/**
 * Agrega las cabeceras de Apigee a la config de una request de axios.
 * @param {Object} config - Config de axios
 * @returns {Object} La misma config
 */
export function applyApigeeHeaders (config) {
  config.headers = { ...config.headers, ...buildApigeeHeaders() }
  return config
}

/**
 * Interceptor de request para la instancia global de axios: solo toca las llamadas a ZS Backend.
 * Es obligatorio filtrar: esa misma instancia llama a otras APIs (pagos, trámites, vivemas…) y enviarles
 * cabeceras propias rompería su CORS.
 * @param {string[]} [zsBackendUrls]
 * @returns {function(Object): Object}
 */
export function createApigeeHeadersInterceptor (zsBackendUrls) {
  return function (config) {
    return isZsBackendRequest(config, zsBackendUrls) ? applyApigeeHeaders(config) : config
  }
}



// WEBPACK FOOTER //
// ./src/utils/apigeeHeaders.js